import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type {
  TrainingAssignAllScope,
  TrainingModuleDraft,
  TrainingModuleKind,
  TrainingModuleSchema,
  TrainingPageDef,
  TrainingRetryMode,
} from '@hcc/types';

/** A version as returned in the modules list (timestamps are ISO strings). */
export interface TrainingModuleVersionSummary {
  id: string;
  module_id: string;
  version: number;
  title: string;
  description: string | null;
  /** 'quiz' (graded course) or 'external' (redirect + self-certify). */
  kind: TrainingModuleKind;
  external_url: string | null;
  external_instructions: string | null;
  external_confirmation: string | null;
  /** False = an assessment that records answers rather than marking them. */
  graded: boolean;
  pass_mark: number;
  retry_mode: TrainingRetryMode;
  /** Question bank: serve N random questions per attempt; null serves all. */
  serve_question_count: number | null;
  shuffle_questions: boolean;
  schema: TrainingModuleSchema;
  published_at: string | null;
  created_at: string;
}

export interface TrainingModuleSummary {
  id: string;
  key: string;
  title: string;
  /** Present when there are unpublished changes saved by the builder. */
  draft: TrainingModuleDraft | null;
  /** Months until a pass must be retaken; null = one-off (never expires). */
  recurrence_months: number | null;
  /** "Available to everyone": reconciled onto every training person by the sweep. */
  assign_to_all: boolean;
  /** Who "everyone" reaches: 'all' (incl. existing staff) or 'new_starters'. */
  assign_to_all_scope: TrainingAssignAllScope;
  /** Email the learner's answers to notify_emails when they complete it. */
  notify_on_completion: boolean;
  notify_emails: string[];
  archived_at: string | null;
  created_at: string;
  versions: TrainingModuleVersionSummary[];
}

const KEY = ['training-modules'] as const;

export function useTrainingModules() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () =>
      (await api<{ modules: TrainingModuleSummary[] }>('/api/admin/training-modules')).modules,
  });
}

export function useCreateTrainingModule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { key: string; title: string }) =>
      api<{ module: TrainingModuleSummary }>('/api/admin/training-modules', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCopyTrainingModule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ module: TrainingModuleSummary }>(`/api/admin/training-modules/${id}/copy`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useRenameTrainingModule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; title: string }) =>
      api<{ module: TrainingModuleSummary }>(`/api/admin/training-modules/${input.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ title: input.title }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * Set (or clear, with null) how often a module must be retaken. Module-level
 * policy, saved straight to the master row, independent of version content.
 */
export function useSetTrainingRecurrence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; recurrence_months: number | null }) =>
      api<{ module: TrainingModuleSummary }>(`/api/admin/training-modules/${input.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ recurrence_months: input.recurrence_months }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export interface TrainingAssignee {
  id: string;
  full_name: string;
  email: string;
  position: string | null;
}

/** People who can be assigned training, including auto-provisioned learners. */
export function useTrainingAssignees() {
  return useQuery({
    queryKey: ['training-assignees'] as const,
    queryFn: async () =>
      (await api<{ people: TrainingAssignee[] }>('/api/admin/training-modules/assignees')).people,
  });
}

/** Assign one module to a set of people (idempotent per person). */
export function useAssignTrainingModule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; person_ids: string[] }) =>
      api<{ people: number; assigned: number }>(`/api/admin/training-modules/${input.id}/assign`, {
        method: 'POST',
        body: JSON.stringify({ person_ids: input.person_ids }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'training-progress'] }),
  });
}

/** Set the "available to everyone" state + scope on a module. */
export function useSetTrainingAssignAll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      assign_to_all: boolean;
      assign_to_all_scope?: TrainingAssignAllScope;
    }) =>
      api<{ module: TrainingModuleSummary }>(`/api/admin/training-modules/${input.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          assign_to_all: input.assign_to_all,
          ...(input.assign_to_all_scope ? { assign_to_all_scope: input.assign_to_all_scope } : {}),
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * "Email the answers when someone completes this." Saved straight to the
 * module (an operational setting, not version content) so changing a
 * recipient never mints a new version.
 */
export function useSetTrainingNotify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; notify_on_completion: boolean; notify_emails: string[] }) =>
      api<{ module: TrainingModuleSummary }>(`/api/admin/training-modules/${input.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          notify_on_completion: input.notify_on_completion,
          notify_emails: input.notify_emails,
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Distinct image URLs already used across training modules (reuse library). */
export function useTrainingMedia(enabled = true) {
  return useQuery({
    queryKey: ['training-media'] as const,
    enabled,
    queryFn: async () =>
      (await api<{ media: string[] }>('/api/admin/training-modules/media')).media,
  });
}

/**
 * A module's allocation targets, as facets. Within a facet the values are OR'd;
 * across facets they are AND'd, and an empty facet means "don't care" — so
 * app_roles ['inductee'] + mobilisation_ids [hcc] means inductees ON the HCC
 * mobilisation, not everyone in either group.
 */
export interface TrainingModuleTargets {
  /** Job roles / trades. */
  role_ids: string[];
  /** Role categories. Same facet as role_ids. */
  category_ids: string[];
  /** Everyone attending these mobilisations, including anyone added later. */
  mobilisation_ids: string[];
  /** Login/permission roles (inductee, hr, coordinator...), not job roles. */
  app_roles: string[];
}

/** A module's allocation targets. */
export function useTrainingModuleTargets(id: string | null) {
  return useQuery({
    queryKey: ['training-module-targets', id] as const,
    enabled: !!id,
    queryFn: async () =>
      (await api<{ targets: TrainingModuleTargets }>(`/api/admin/training-modules/${id}/targets`))
        .targets,
  });
}

/** Replace a module's role/category targets. */
export function useSetTrainingModuleTargets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string } & TrainingModuleTargets) =>
      api<{ targets: TrainingModuleTargets }>(`/api/admin/training-modules/${input.id}/targets`, {
        method: 'PUT',
        body: JSON.stringify({
          role_ids: input.role_ids,
          category_ids: input.category_ids,
          mobilisation_ids: input.mobilisation_ids,
          app_roles: input.app_roles,
        }),
      }),
    onSuccess: (_data, v) => {
      qc.invalidateQueries({ queryKey: ['training-module-targets', v.id] });
      qc.invalidateQueries({ queryKey: ['admin', 'training-progress'] });
    },
  });
}

export interface SaveTrainingDraftInput {
  id: string;
  title: string;
  description: string;
  kind?: TrainingModuleKind;
  external_url?: string | null;
  external_instructions?: string | null;
  external_confirmation?: string | null;
  graded?: boolean;
  pass_mark: number;
  retry_mode: TrainingRetryMode;
  serve_question_count: number | null;
  shuffle_questions: boolean;
  schema: TrainingModuleSchema;
}

export function useSaveTrainingModuleDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...draft }: SaveTrainingDraftInput) =>
      api<{ ok: true }>(`/api/admin/training-modules/${id}/draft`, {
        method: 'PUT',
        body: JSON.stringify(draft),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export interface PublishTrainingVersionInput {
  id: string;
  title: string;
  description?: string;
  kind?: TrainingModuleKind;
  external_url?: string;
  external_instructions?: string;
  external_confirmation?: string;
  graded?: boolean;
  pass_mark: number;
  retry_mode: TrainingRetryMode;
  serve_question_count: number | null;
  shuffle_questions: boolean;
  schema: TrainingModuleSchema;
}

export function usePublishTrainingModule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: PublishTrainingVersionInput) =>
      api<{ version: TrainingModuleVersionSummary }>(`/api/admin/training-modules/${id}/versions`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useArchiveTrainingModule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/api/admin/training-modules/${id}/archive`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/* -------------------------------------------------------------------------
 * AI builder. The "Generate with AI" tools; only shown when the server has an
 * API key configured (useAiBuilderAvailable). Output is always inserted as an
 * editable draft — never published automatically.
 * -----------------------------------------------------------------------*/

/** Whether the server has the AI builder configured (feature probe). */
export function useAiBuilderAvailable() {
  return useQuery({
    queryKey: ['training-ai-available'] as const,
    staleTime: 5 * 60 * 1000,
    queryFn: async () =>
      (await api<{ available: boolean }>('/api/admin/training-modules/ai/available')).available,
  });
}

/** A photo of a slide for AI page generation: base64 (no data: prefix) + type. */
export interface AiImagePayload {
  data: string;
  media_type: 'image/png' | 'image/jpeg' | 'image/webp' | 'image/gif';
}

export interface GenerateAiPageInput {
  description?: string;
  image?: AiImagePayload;
  /** The module being authored; false means generate no answer key. */
  graded?: boolean;
}

/** Generate one (or a few) pages from a photo and/or a description. */
export function useGenerateAiPage() {
  return useMutation({
    mutationFn: (input: GenerateAiPageInput) =>
      api<{ pages: TrainingPageDef[] }>('/api/admin/training-modules/ai/page', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
  });
}

export interface AiCourseDraft {
  title?: string;
  description?: string;
  /** False when the model judged this an assessment rather than a test. */
  graded?: boolean;
  pass_mark: number;
  schema: TrainingModuleSchema;
}

/** Generate a whole course (slides + questions + settings) from source text. */
export function useGenerateAiCourse() {
  return useMutation({
    mutationFn: (description: string) =>
      api<{ draft: AiCourseDraft }>('/api/admin/training-modules/ai/course', {
        method: 'POST',
        body: JSON.stringify({ description }),
      }),
  });
}
